import { defineStore } from "pinia";
import { useUsuariosStore } from "./usuarios";
import { useTurnosStore } from "./turnos";
import { storage } from "@/storage";
import { STORAGE_KEYS } from "@/storage/keys";

import { collection, doc, setDoc, updateDoc, deleteDoc, deleteField, query, where, arrayUnion, arrayRemove, runTransaction, serverTimestamp, onSnapshot } from "firebase/firestore";
import { db } from "@/firebase/firestore";
import { firebaseStorage } from "@/firebase/storage";
import { ref as storageRef, deleteObject, uploadString, getDownloadURL } from "firebase/storage";
import { useToast } from "@/composables/useToast";
import { capitalizarNombre } from "@/utils/texto";
import {
  normalizarPlanes, crearPlanVacio, crearItemDeTratamiento, nuevoPago, distribuirCascada,
  opAddPlan, opRemovePlan, opSetEstado, opAddItem, opRemoveItem, opSetDescuento,
  opAddPagos, opRemovePago, opMovePago,
} from "@/utils/planesPresupuesto";

// Envuelve una escritura crítica con un timeout: si Firestore no confirma en `ms`, la promesa
// se RECHAZA en vez de quedar colgada para siempre (caso "conexión zombie": WiFi conectado pero
// sin internet real). Así el cuelgue se convierte en un error visible que preserva el texto.
// Si el timeout dispara, la escritura puede completarse igual más tarde (el SDK la mantiene
// encolada mientras la pestaña siga viva); para evoluciones/comentarios eso es inocuo.
function conTimeout(promesa, ms = 15000) {
  return Promise.race([
    promesa,
    new Promise((_, reject) => setTimeout(() => reject(new Error("timeout")), ms)),
  ]);
}

export const usePacientesStore = defineStore("pacientes", {
  state: () => ({
    pacientes: [],
    tratamientos: [],  // Biblioteca global de tratamientos
    loading: false,
    error: null,
    modoOffline: false,
    _unsubscribePacientes: null,
    _unsubscribeTratamientos: null,
    _reintentoTimer: null,
    _reintentoDelay: 5000,
    // Fase 2: evoluciones de la subcolección del paciente que se está viendo.
    historiaActual: [],
    _unsubscribeHistoria: null,
  }),

  actions: {
    iniciarEscucha() {
      const usuariosStore = useUsuariosStore();
      const usuarioId = usuariosStore.usuarioActual?.id || usuariosStore.usuarioActual?.uid;
      if (!usuarioId) return;

      // Desuscribir listeners anteriores si existen
      if (this._unsubscribePacientes) {
        this._unsubscribePacientes();
        this._unsubscribePacientes = null;
      }
      if (this._unsubscribeTratamientos) {
        this._unsubscribeTratamientos();
        this._unsubscribeTratamientos = null;
      }

      // --- Listener 1: pacientes ---
      this._unsubscribePacientes = onSnapshot(
        query(collection(db, "pacientes"), where("usuarioId", "==", usuarioId)),
        { includeMetadataChanges: true },
        async (snapshot) => {
          if (snapshot.metadata.hasPendingWrites) return;
          // Ignorar snapshots vacíos que vienen SOLO de la caché: Firestore suele
          // entregar un primer snapshot vacío desde caché antes de responder el servidor.
          // Tratar ese vacío como real disparaba la reescritura desde localStorage y
          // pisaba datos buenos (causa histórica de pérdida de historias).
          if (snapshot.empty && snapshot.metadata.fromCache) return;

          if (!snapshot.empty) {
            this.pacientes = snapshot.docs
              .map((doc) => {
                const paciente = {
                  notas: [],
                  archivos: [],
                  turnos: [],
                  presupuesto: [],
                  id: Number(doc.id),
                  ...doc.data(),
                };
                // Planes de tratamiento: exponer siempre `presupuestos` (formato nuevo),
                // derivándolo del `presupuesto` viejo si hace falta. Lectura dual: la app
                // funciona con cualquiera de los dos formatos guardados en Firestore.
                const { presupuestos, proximoPlanNum } = normalizarPlanes(paciente);
                paciente.presupuestos = presupuestos;
                paciente.proximoPlanNum = proximoPlanNum;
                return paciente;
              })
              .filter((p) => p.usuarioId === usuarioId && !p.eliminado);
            this.guardarEnLocalStorage();
          } else {
            // El servidor confirma que no hay pacientes para este usuario.
            // NO reescribimos desde localStorage: hacerlo pisaba el servidor con
            // copias viejas/vacías y era la causa de que desaparecieran historias.
            // Si fuese un vacío transitorio, el próximo snapshot vuelve a poblar el estado.
            this.pacientes = [];
          }

          this.modoOffline = false;
          this.error = null;
          this.loading = false;
          // Conexión OK: cancelar cualquier reintento pendiente y resetear el backoff.
          this._reintentoDelay = 5000;
          if (this._reintentoTimer) { clearTimeout(this._reintentoTimer); this._reintentoTimer = null; }
        },
        (e) => {
          console.warn("onSnapshot pacientes falló, usando localStorage:", e);
          this.modoOffline = true;
          this.error = "Sin conexión a la nube. Reintentando…";
          const data = storage.get(STORAGE_KEYS.PACIENTES);
          if (data && data.length > 0) {
            this.pacientes = data.filter((p) => p.usuarioId === usuarioId && !p.eliminado);
          } else {
            this.pacientes = [];
          }
          this.loading = false;
          // Reintento automático con backoff (5s → 15s → 45s → 60s): sin esto, un blip de red
          // dejaba la app en solo-lectura hasta recargar a mano. iniciarEscucha re-suscribe solo.
          if (this._reintentoTimer) clearTimeout(this._reintentoTimer);
          const delay = this._reintentoDelay;
          this._reintentoDelay = Math.min(this._reintentoDelay * 3, 60000);
          this._reintentoTimer = setTimeout(() => {
            this._reintentoTimer = null;
            this.iniciarEscucha();
          }, delay);
        }
      );

      // --- Listener 2: tratamientos ---
      this._unsubscribeTratamientos = onSnapshot(
        query(collection(db, "tratamientos"), where("usuarioId", "==", usuarioId)),
        { includeMetadataChanges: true },
        (snapshot) => {
          if (snapshot.metadata.hasPendingWrites) return;
          // Ignorar el vacío que viene SOLO de caché (transitorio), igual que en pacientes.
          if (snapshot.empty && snapshot.metadata.fromCache) return;

          if (!snapshot.empty) {
            this.tratamientos = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
            // Mantener cache local fresco
            const tratExistentes = storage.get(STORAGE_KEYS.TRATAMIENTOS) || [];
            const tratOtros = tratExistentes.filter((t) => t.usuarioId && t.usuarioId !== usuarioId);
            storage.set(STORAGE_KEYS.TRATAMIENTOS, [...tratOtros, ...this.tratamientos]);
          } else {
            // El servidor confirma que no hay tratamientos: NO reescribimos desde localStorage.
            // Hacerlo (la vieja migración) pisaba la biblioteca con copias viejas — mismo
            // motor que borraba historias en pacientes.
            this.tratamientos = [];
          }
        },
        (e) => {
          console.warn("onSnapshot tratamientos falló, usando localStorage:", e);
          const tratamientosData = storage.get(STORAGE_KEYS.TRATAMIENTOS);
          this.tratamientos = (tratamientosData || []).filter(
            (t) => !t.usuarioId || t.usuarioId === usuarioId
          );
        }
      );
    },

    _verificarConexion() {
      // Online-only: bloqueamos escrituras si no hay conexión, para que un dato escrito
      // sin internet NO quede solo en memoria y se pierda al recargar (nunca llegaba a la nube).
      if (this.modoOffline || !navigator.onLine) {
        const { error: toastError } = useToast();
        toastError("Sin conexión: no se guardaron los cambios.");
        return false;
      }
      return true;
    },

    reintentarConexion() {
      this.modoOffline = false;
      this.error = null;
      this.cargarDesdeLocalStorage();
    },

    cargarDesdeLocalStorage() {
      this.loading = true;
      this.error = null;
      const usuariosStore = useUsuariosStore();
      const usuarioId = usuariosStore.usuarioActual?.id || usuariosStore.usuarioActual?.uid;
      if (!usuarioId) {
        this.loading = false;
        return;
      }
      this.iniciarEscucha();
    },

    detenerEscucha() {
      if (this._unsubscribePacientes) {
        this._unsubscribePacientes();
        this._unsubscribePacientes = null;
      }
      if (this._unsubscribeTratamientos) {
        this._unsubscribeTratamientos();
        this._unsubscribeTratamientos = null;
      }
      if (this._reintentoTimer) { clearTimeout(this._reintentoTimer); this._reintentoTimer = null; }
    },

    // Fase 2: escuchar la subcolección `historia` del paciente que se está viendo.
    // Solo se suscribe al paciente abierto (no a todos), para no leer de más.
    escucharHistoria(idPaciente) {
      this.detenerEscuchaHistoria();
      const usuariosStore = useUsuariosStore();
      const usuarioId = usuariosStore.usuarioActual?.id || usuariosStore.usuarioActual?.uid;
      if (!usuarioId || idPaciente == null) return;

      const ref = collection(db, "pacientes", String(idPaciente), "historia");
      this._unsubscribeHistoria = onSnapshot(
        query(ref, where("usuarioId", "==", usuarioId)),
        { includeMetadataChanges: true },
        (snapshot) => {
          if (snapshot.metadata.hasPendingWrites) return;
          this.historiaActual = snapshot.docs
            .map((d) => ({ ...d.data(), id: d.id }))
            .filter((n) => !n.eliminado);
        },
        (e) => {
          console.warn("onSnapshot historia falló:", e);
        }
      );
    },

    detenerEscuchaHistoria() {
      if (this._unsubscribeHistoria) {
        this._unsubscribeHistoria();
        this._unsubscribeHistoria = null;
      }
      this.historiaActual = [];
    },


    guardarEnLocalStorage() {
      const usuariosStore = useUsuariosStore();
      const usuarioId = usuariosStore.usuarioActual?.id || usuariosStore.usuarioActual?.uid;
      // Preservar pacientes de otros usuarios en el mismo dispositivo
      const pacientesExistentes = storage.get(STORAGE_KEYS.PACIENTES) || [];
      const pacientesDeOtrosUsuarios = pacientesExistentes.filter(
        (p) => p.usuarioId && p.usuarioId !== usuarioId
      );
      storage.set(STORAGE_KEYS.PACIENTES, [...pacientesDeOtrosUsuarios, ...this.pacientes]);
      // Guardar tratamientos preservando los de otros usuarios en el mismo dispositivo
      const tratamientosExistentes = storage.get(STORAGE_KEYS.TRATAMIENTOS) || [];
      const tratamientosDeOtrosUsuarios = tratamientosExistentes.filter(
        (t) => t.usuarioId && t.usuarioId !== usuarioId
      );
      storage.set(STORAGE_KEYS.TRATAMIENTOS, [...tratamientosDeOtrosUsuarios, ...this.tratamientos]);
    },

    async guardarPacienteEnFirestore(paciente) {
      try {
        const pacientesRef = collection(db, "pacientes");
        const id = String(paciente.id);
        await setDoc(doc(pacientesRef, id), paciente, { merge: true });
        return true;
      } catch (e) {
        console.warn("No se pudo guardar en Firestore:", e);
        const { error: toastError } = useToast();
        toastError("No se pudo guardar en la nube. Revisá tu conexión y volvé a intentarlo.");
        return false;
      }
    },

    // Actualiza SOLO los campos indicados en Firestore sin tocar el resto del documento.
    // Esto evita que un guardado parcial borre archivos, notas u otros datos existentes.
    async actualizarCamposEnFirestore(id, campos) {
      try {
        const docRef = doc(db, "pacientes", String(id));
        await updateDoc(docRef, campos);
        return true;
      } catch (e) {
        // Si el documento no existe aún, crearlo. Al no existir en el servidor, no hay nada
        // que pisar. Solo recreamos si tenemos el paciente con usuarioId (las reglas de
        // Firestore exigen usuarioId para crear; sin él, el setDoc fallaría igual).
        if (e.code === "not-found") {
          const paciente = this.pacientes.find((p) => p.id === id);
          if (paciente?.usuarioId) {
            await setDoc(doc(db, "pacientes", String(id)), paciente, { merge: true });
            return true;
          }
          return false;
        } else {
          console.warn("No se pudo actualizar en Firestore:", e);
          const { error: toastError } = useToast();
          toastError("No se pudo guardar en la nube. Revisá tu conexión y volvé a intentarlo.");
          return false;
        }
      }
    },

    // Modifica un elemento dentro de un array de un documento de paciente de forma atómica.
    // Lee el doc fresco del servidor, aplica los cambios al elemento que matchea `predicate`
    // y escribe el array modificado. Firestore reintenta automáticamente si hay conflictos.
    async editarElementoEnArrayConTransaccion(idPaciente, campo, predicate, cambios) {
      try {
        const docRef = doc(db, "pacientes", String(idPaciente));
        await runTransaction(db, async (tx) => {
          const snap = await tx.get(docRef);
          if (!snap.exists()) return;
          const data = snap.data();
          const arr = Array.isArray(data[campo]) ? [...data[campo]] : [];
          const idx = arr.findIndex(predicate);
          if (idx === -1) return;
          arr[idx] = { ...arr[idx], ...cambios };
          tx.update(docRef, { [campo]: arr });
        });
        return true;
      } catch (e) {
        console.warn("No se pudo editar elemento en Firestore:", e);
        const { error: toastError } = useToast();
        toastError("No se pudo guardar el cambio en la nube. Revisá tu conexión y volvé a intentarlo.");
        return false;
      }
    },

    // Persiste la migración del presupuesto viejo (array plano `presupuesto`) al formato
    // de planes (`presupuestos`), de forma ATÓMICA: un solo update que escribe `presupuestos`
    // + `proximoPlanNum` y borra `presupuesto`. Firestore lo hace todo-o-nada: si algo falla,
    // el formato viejo queda intacto y no se pierde nada. Online-only (no fuerza sin conexión).
    // Se llama al abrir la ficha del paciente.
    async migrarPlanesEnFirestore(idPaciente) {
      if (this.modoOffline || !navigator.onLine) return;
      try {
        const docRef = doc(db, "pacientes", String(idPaciente));
        await runTransaction(db, async (tx) => {
          const snap = await tx.get(docRef);
          if (!snap.exists()) return;
          const data = snap.data();
          if (Array.isArray(data.presupuestos)) return; // ya migrado en el servidor
          const { presupuestos, proximoPlanNum, migrado } = normalizarPlanes(data);
          if (!migrado) return; // no hay presupuesto viejo con datos: no tocamos el doc
          tx.update(docRef, {
            presupuestos,
            proximoPlanNum,
            presupuesto: deleteField(),
          });
        });
      } catch (e) {
        // Si falla, no pasa nada: el formato viejo sigue ahí y se reintenta la próxima vez.
        console.warn("No se pudo migrar el presupuesto a planes:", e);
      }
    },

    async eliminarPacienteDeFirestore(id) {
      try {
        await updateDoc(doc(db, "pacientes", String(id)), {
          eliminado: true,
          eliminadoEn: serverTimestamp(),
        });
      } catch (e) {
        console.warn("No se pudo eliminar de Firestore:", e);
        const { error: toastError } = useToast();
        toastError("No se pudo eliminar en la nube. Intentá de nuevo.");
      }
    },

    async guardarTratamientoEnFirestore(tratamiento) {
      try {
        await setDoc(doc(db, "tratamientos", String(tratamiento.id)), tratamiento, { merge: true });
      } catch (e) {
        console.warn("No se pudo guardar tratamiento en Firestore:", e);
        const { error: toastError } = useToast();
        toastError("No se pudo guardar el tratamiento en la nube. Revisá tu conexión y volvé a intentarlo.");
      }
    },

    async eliminarTratamientoDeFirestore(id) {
      try {
        await deleteDoc(doc(db, "tratamientos", String(id)));
      } catch (e) {
        console.warn("No se pudo eliminar tratamiento de Firestore:", e);
        const { error: toastError } = useToast();
        toastError("No se pudo eliminar el tratamiento en la nube. Intentá de nuevo.");
      }
    },

    async agregarPaciente(paciente) {
      if (!this._verificarConexion()) return;
      // Obtener usuario actual
      const usuariosStore = useUsuariosStore();
      const usuarioId = usuariosStore.usuarioActual?.id || usuariosStore.usuarioActual?.uid;
      // Sin usuarioId no creamos el paciente: quedaría "huérfano" y las reglas de
      // Firestore bloquearían en silencio toda edición futura (se perderían los datos).
      if (!usuarioId) {
        throw new Error("No se pudo identificar tu usuario. Recargá la página e intentá de nuevo.");
      }

      // Validar campos obligatorios
      const nombreTrimmed = capitalizarNombre(paciente.nombre);
      if (!nombreTrimmed) {
        throw new Error("El nombre del paciente es obligatorio.");
      }

      // Validar que no exista la cédula (si se ingresó una)
      if (paciente.ci) {
        const ciInput = String(paciente.ci).trim();
        if (ciInput) {
          const existe = this.pacientes.find(
            (p) => String(p.ci || "").trim() === ciInput,
          );
          if (existe) {
            throw new Error(
              "Ya existe un paciente registrado con esa Cédula/ID.",
            );
          }
          paciente.ci = ciInput; // Guardar limpio
        }
      }

      const nuevoPaciente = {
        id: Date.now() * 1000 + Math.floor(Math.random() * 1000),
        activo: true,
        nombre: nombreTrimmed,
        // Apellido como campo propio (normalizado igual que el nombre para que su
        // sufijo calce al reconstruir los campos del formulario). Ver utils/nombrePaciente.js.
        apellido: capitalizarNombre(paciente.apellido || ""),
        telefono: paciente.telefono || "",
        ci: paciente.ci || "",
        alergias: paciente.alergias || "",
        observaciones: paciente.observaciones || "",
        enfermedades: paciente.enfermedades || "",
        notas: [],
        turnos: [],
        archivos: [],
        presupuesto: [],
        usuarioId,
      };
      // Confirmar en la nube ANTES de reflejar en la UI: así nunca queda un paciente
      // "fantasma" visible que no llegó al servidor (evita historias y turnos huérfanos).
      const guardadoOk = await this.guardarPacienteEnFirestore(nuevoPaciente);
      if (!guardadoOk) {
        throw new Error("No se pudo crear el paciente en la nube. Revisá tu conexión y volvé a intentarlo.");
      }
      this.pacientes.push(nuevoPaciente);
      this.guardarEnLocalStorage();
      const { success } = useToast();
      success("Paciente creado correctamente");
      return nuevoPaciente;
    },

    // Crear paciente si no existe (para cuando se crea un turno sin paciente previo)
    async crearPacienteObtenerExistente(nombrePaciente) {
      // Buscar si ya existe un paciente con ese nombre (solo del usuario actual)
      let paciente = this.pacientes.find(
        (p) => p.nombre.toLowerCase() === nombrePaciente.toLowerCase(),
      );

      if (!paciente) {
        // Obtener usuarioId para asociar el paciente al usuario actual
        const usuariosStore = useUsuariosStore();
        const usuarioId = usuariosStore.usuarioActual?.id || usuariosStore.usuarioActual?.uid;

        // Sin usuarioId no creamos el paciente: quedaría huérfano y sus datos no
        // se podrían guardar. Devolvemos null y el llamador no crea el turno.
        if (!usuarioId) return null;

        // Si no existe, crear uno nuevo con usuarioId
        const nuevoPaciente = {
          id: Date.now() * 1000 + Math.floor(Math.random() * 1000),
          nombre: capitalizarNombre(nombrePaciente),
          telefono: "",
          ci: "",
          alergias: "",
          observaciones: "",
          enfermedades: "",
          notas: [],
          turnos: [],
          archivos: [],
          presupuesto: [],
          usuarioId, // ← asegurar aislamiento por usuario
        };
        // Confirmar en la nube antes de reflejar: si falla, no dejamos un paciente
        // fantasma ni creamos el turno (el llamador interpreta null como "no crear").
        const guardadoOk = await this.guardarPacienteEnFirestore(nuevoPaciente);
        if (!guardadoOk) return null;
        this.pacientes.push(nuevoPaciente);
        this.guardarEnLocalStorage();
        paciente = nuevoPaciente;
      }

      return paciente;
    },

    editarPaciente(id, datos) {
      if (!this._verificarConexion()) return;
      const usuariosStore = useUsuariosStore();
      const usuarioId = usuariosStore.usuarioActual?.id || usuariosStore.usuarioActual?.uid;
      const paciente = this.pacientes.find((p) => p.id === id && p.usuarioId === usuarioId);
      if (!paciente) return;

      // Validar que no se duplique la cédula al editar
      if (datos.ci !== undefined) {
        const ciInput = String(datos.ci || "").trim();
        const ciActual = String(paciente.ci || "").trim();

        if (ciInput && ciInput !== ciActual) {
          const existe = this.pacientes.find(
            (p) => String(p.ci || "").trim() === ciInput && p.id !== id,
          );
          if (existe) {
            throw new Error(
              "Ya existe otro paciente registrado con esa Cédula/ID.",
            );
          }
        }
        datos.ci = ciInput; // Guardar limpio
      }

      // Normalizar el nombre a Mayúscula Inicial (aplica también al renombrado de turnos).
      if (datos.nombre !== undefined) {
        datos.nombre = capitalizarNombre(datos.nombre);
      }
      // Mismo tratamiento para el apellido: así su sufijo calza con el del nombre
      // completo al reconstruir los campos del formulario (utils/nombrePaciente.js).
      if (datos.apellido !== undefined) {
        datos.apellido = capitalizarNombre(datos.apellido);
      }

      // Snapshot de los valores previos de los campos que se editan, para poder revertir si la
      // nube rechaza el guardado (P1-3). Sin esto, un cambio fallido se mostraba como guardado
      // y después el snapshot del servidor lo revertía solo, en silencio (grave con alergias).
      const valoresPrevios = {};
      for (const campo of Object.keys(datos)) valoresPrevios[campo] = paciente[campo];

      Object.assign(paciente, datos);

      this.guardarEnLocalStorage();
      // Campos simples por updateDoc. NO mandamos el array `turnos` completo acá:
      // reescribirlo entero podía pisar turnos agregados desde otro dispositivo (P9).
      // Aviso "Cambios guardados" solo cuando la nube confirma; si falla, revertimos.
      this.actualizarCamposEnFirestore(paciente.id, datos).then((ok) => {
        if (ok) {
          const { success } = useToast();
          success("Cambios guardados");
        } else {
          // La nube no confirmó: revertir el cambio local para no mostrarlo como guardado.
          // (actualizarCamposEnFirestore ya avisó del error con su propio toast.)
          Object.assign(paciente, valoresPrevios);
          this.guardarEnLocalStorage();
        }
      });

      // 1. Si cambió el nombre, renombrar al paciente en cada turno del array interno.
      //    Con una transacción que lee el array FRESCO del servidor y solo cambia el
      //    nombre (no reescribe con la copia local) para no perder turnos de otro dispositivo.
      if (datos.nombre && Array.isArray(paciente.turnos)) {
        paciente.turnos.forEach((t) => { t.paciente = datos.nombre; });
        this.guardarEnLocalStorage();
        (async () => {
          try {
            const docRef = doc(db, "pacientes", String(paciente.id));
            await runTransaction(db, async (tx) => {
              const snap = await tx.get(docRef);
              if (!snap.exists()) return;
              const arr = Array.isArray(snap.data().turnos) ? snap.data().turnos : [];
              if (arr.length === 0) return;
              tx.update(docRef, { turnos: arr.map((t) => ({ ...t, paciente: datos.nombre })) });
            });
          } catch (e) {
            console.warn("No se pudo renombrar al paciente en sus turnos:", e);
          }
        })();
      }

      // 2. Actualizar también en la store global de turnos (Agenda).
      //    Importante: persistir el nombre nuevo en CADA cita en Firestore, no solo en
      //    memoria/localStorage. Antes solo se cambiaba local y, al refrescar el snapshot,
      //    el servidor devolvía el nombre viejo y se perdía el renombrado en la Agenda.
      if (datos.nombre) {
        const turnosStore = useTurnosStore();
        // Aseguramos cargar los turnos por si no se ha visitado la agenda aún
        if (!turnosStore.turnos || turnosStore.turnos.length === 0) {
          turnosStore.cargarDesdeLocalStorage();
        }

        turnosStore.turnos
          .filter((t) => t.pacienteId === id)
          .forEach((t) => turnosStore.actualizarTurno(t.id, { paciente: datos.nombre }));
      }
    },

    // Sube la foto de perfil del paciente a Storage y guarda en el documento SOLO la URL
    // (no el base64). Antes la foto se guardaba como base64 dentro del doc, lo que lo
    // inflaba y hacía que el listado de pacientes la descargara entera en cada arranque.
    // Devuelve true solo si la nube confirmó. Las fotos viejas (base64, sin fotoRef) se
    // siguen mostrando igual; este cambio solo aplica a las fotos nuevas.
    async actualizarFotoPaciente(idPaciente, dataUrl) {
      if (!this._verificarConexion()) return false;
      const usuariosStore = useUsuariosStore();
      const usuarioId = usuariosStore.usuarioActual?.id || usuariosStore.usuarioActual?.uid;
      const paciente = this.pacientes.find((p) => p.id === idPaciente && p.usuarioId === usuarioId);
      if (!paciente || !usuarioId) return false;

      const refAnterior = paciente.fotoRef || null;
      // Mismo árbol que los archivos del paciente (ya cubierto por las reglas de Storage).
      // Nombre único por subida: así siempre es una creación (las reglas no permiten update).
      const path = `archivos/${usuarioId}/${idPaciente}/foto_${Date.now()}.jpg`;
      try {
        const fileRef = storageRef(firebaseStorage, path);
        await uploadString(fileRef, dataUrl, "data_url");
        const url = await getDownloadURL(fileRef);
        await updateDoc(doc(db, "pacientes", String(idPaciente)), { foto: url, fotoRef: path });
        paciente.foto = url;
        paciente.fotoRef = path;
        this.guardarEnLocalStorage();
        // Borrar la foto anterior de Storage si era una subida previa (las base64 viejas
        // no tienen fotoRef, así que no hay nada que borrar en ese caso).
        if (refAnterior) {
          try {
            await deleteObject(storageRef(firebaseStorage, refAnterior));
          } catch (e) {
            console.warn("No se pudo borrar la foto anterior de Storage:", e);
          }
        }
        return true;
      } catch (e) {
        console.warn("No se pudo guardar la foto en la nube:", e);
        const { error: toastError } = useToast();
        toastError("No se pudo guardar la foto. Revisá tu conexión y volvé a intentarlo.");
        return false;
      }
    },

    // Fase 2: las evoluciones NUEVAS se guardan en la subcolección `historia` (cada una su
    // propio documento). NO tocan el array viejo. Devuelve true solo si Firestore confirmó,
    // para mostrar "✓ Guardado" de verdad.
    async agregarNota(idPaciente, nota, fechaPersonalizada = null) {
      if (!this._verificarConexion()) return false;
      const usuariosStore = useUsuariosStore();
      const usuarioId = usuariosStore.usuarioActual?.id || usuariosStore.usuarioActual?.uid;
      if (!usuarioId) return false;

      // Si viene fecha personalizada (YYYY-MM-DD), guardarla al mediodía local
      // para evitar desfases de zona horaria al mostrar con toLocaleDateString()
      const fechaISO = fechaPersonalizada
        ? new Date(fechaPersonalizada + 'T12:00:00').toISOString()
        : new Date().toISOString();

      const notaId = crypto.randomUUID();
      // creadoEn autoritativo del SERVIDOR (no del reloj del celular): así la marca de auditoría
      // no se puede falsear cambiando la hora del dispositivo y la ventana de edición es fiable.
      const nuevaNota = { id: notaId, texto: nota, fecha: fechaISO, usuarioId, creadoEn: serverTimestamp() };

      try {
        await conTimeout(setDoc(doc(db, "pacientes", String(idPaciente), "historia", notaId), nuevaNota));
        // Reflejo optimista para que aparezca al instante. serverTimestamp() no tiene valor local
        // (se resuelve en el servidor), así que para mostrar/editar YA usamos la hora local; el
        // listener luego trae el creadoEn real del servidor y reemplaza esta copia.
        this.historiaActual.push({ ...nuevaNota, creadoEn: new Date().toISOString() });
        return true;
      } catch (e) {
        console.warn("No se pudo guardar la evolución en Firestore:", e);
        const { error: toastError } = useToast();
        toastError("No se pudo guardar la evolución en la nube. No se perdió tu texto, probá de nuevo.");
        return false;
      }
    },

    // Devuelve true solo si Firestore confirmó el cambio (para mostrar "✓ Guardado").
    // notaRef = { id, fecha, texto }: matchea por id único (las evoluciones nuevas lo tienen);
    // para notas viejas sin id, por fecha + texto original. Así NUNCA edita otra evolución del
    // mismo día (que comparte la misma fecha fijada al mediodía).
    async actualizarNota(idPaciente, notaRef, nuevoTexto) {
      if (!this._verificarConexion()) return false;
      const ref = (notaRef && typeof notaRef === "object") ? notaRef : { fecha: notaRef };

      // Caso 1: la evolución vive en la subcolección (las nuevas y las ya migradas).
      if (ref.id && this.historiaActual.some((n) => n.id === ref.id)) {
        try {
          await conTimeout(updateDoc(doc(db, "pacientes", String(idPaciente), "historia", ref.id), { texto: nuevoTexto }));
          const local = this.historiaActual.find((n) => n.id === ref.id);
          if (local) local.texto = nuevoTexto;
          return true;
        } catch (e) {
          console.warn("No se pudo editar la evolución en Firestore:", e);
          const { error: toastError } = useToast();
          toastError("No se pudo guardar el cambio en la nube. Probá de nuevo.");
          return false;
        }
      }

      // Caso 2: evolución vieja todavía en el array del paciente (transacción sobre el array).
      const paciente = this.pacientes.find((p) => p.id === idPaciente);
      if (!paciente || !paciente.notas) return false;
      const matchNota = (n) =>
        ref.id ? n.id === ref.id : (n.fecha === ref.fecha && n.texto === ref.texto);
      const nota = paciente.notas.find(matchNota);
      if (!nota) return false;
      nota.texto = nuevoTexto;
      this.guardarEnLocalStorage();
      return await this.editarElementoEnArrayConTransaccion(
        paciente.id,
        "notas",
        matchNota,
        { texto: nuevoTexto }
      );
    },

    // Devuelve true solo si Firestore confirmó el guardado (igual que las evoluciones).
    async agregarComentario(idPaciente, texto) {
      if (!this._verificarConexion()) return false;
      const paciente = this.pacientes.find((p) => p.id === idPaciente);
      if (!paciente) return false;
      const nuevoComentario = { id: crypto.randomUUID(), texto, fecha: new Date().toISOString() };
      // Escribe PRIMERO en Firestore y refleja en local solo si confirma. arrayUnion solo agrega.
      try {
        await conTimeout(updateDoc(doc(db, "pacientes", String(idPaciente)), {
          comentarios: arrayUnion(nuevoComentario),
        }));
        if (!Array.isArray(paciente.comentarios)) paciente.comentarios = [];
        paciente.comentarios.push(nuevoComentario);
        this.guardarEnLocalStorage();
        return true;
      } catch (e) {
        console.warn("No se pudo guardar el comentario en Firestore:", e);
        const { error: toastError } = useToast();
        toastError("No se pudo guardar el comentario en la nube. No se perdió tu texto, probá de nuevo.");
        return false;
      }
    },

    // comentarioRef = { id, fecha, texto }: matchea por id único; para comentarios viejos
    // sin id, por fecha + texto original. Evita editar el comentario equivocado del mismo día.
    async editarComentario(idPaciente, comentarioRef, nuevoTexto) {
      if (!this._verificarConexion()) return false;
      const paciente = this.pacientes.find((p) => p.id === idPaciente);
      if (!paciente?.comentarios) return false;
      const ref = (comentarioRef && typeof comentarioRef === "object") ? comentarioRef : { fecha: comentarioRef };
      const matchCom = (c) =>
        ref.id ? c.id === ref.id : (c.fecha === ref.fecha && c.texto === ref.texto);
      const comentario = paciente.comentarios.find(matchCom);
      if (!comentario) return false;
      comentario.texto = nuevoTexto;
      this.guardarEnLocalStorage();
      return await this.editarElementoEnArrayConTransaccion(
        paciente.id,
        "comentarios",
        matchCom,
        { texto: nuevoTexto }
      );
    },

    eliminarNota(idPaciente, fechaNota) {
      if (!this._verificarConexion()) return;
      const paciente = this.pacientes.find((p) => p.id === idPaciente);
      if (!paciente || !paciente.notas) return;

      const notaAEliminar = paciente.notas.find((n) => n.fecha === fechaNota);
      if (!notaAEliminar) return;

      paciente.notas = paciente.notas.filter((n) => n.fecha !== fechaNota);
      this.guardarEnLocalStorage();
      this.actualizarCamposEnFirestore(paciente.id, { notas: arrayRemove(notaAEliminar) });
    },

    deshacerUltimaNota(idPaciente) {
      if (!this._verificarConexion()) return;
      const paciente = this.pacientes.find((p) => p.id === idPaciente);
      if (!paciente || !paciente.notas || paciente.notas.length === 0) return;

      const ultimaNota = paciente.notas[paciente.notas.length - 1];
      paciente.notas.pop();
      this.guardarEnLocalStorage();
      this.actualizarCamposEnFirestore(paciente.id, { notas: arrayRemove(ultimaNota) });
    },

    agregarTurno(idPaciente, turno) {
      if (!this._verificarConexion()) return;
      const paciente = this.pacientes.find((p) => p.id === idPaciente);
      if (paciente) {
        paciente.turnos.push(turno);
        this.guardarEnLocalStorage();
        this.actualizarCamposEnFirestore(paciente.id, { turnos: arrayUnion(turno) });
      }
    },

    actualizarTurno(idPaciente, idTurno, datos) {
      if (!this._verificarConexion()) return;
      const paciente = this.pacientes.find((p) => p.id === idPaciente);
      if (paciente && paciente.turnos) {
        const turno = paciente.turnos.find((t) => t.id === idTurno);
        if (turno) {
          Object.assign(turno, datos);
          this.guardarEnLocalStorage();
          this.editarElementoEnArrayConTransaccion(
            paciente.id,
            "turnos",
            (t) => t.id === idTurno,
            datos
          );
        }
      }
    },

    async agregarArchivo(idPaciente, archivo) {
      if (!this._verificarConexion()) return;
      const paciente = this.pacientes.find((p) => p.id === idPaciente);
      if (paciente) {
        paciente.archivos.push(archivo);
        this.guardarEnLocalStorage();

        // Write directo para poder propagar errores y permitir compensación en Storage.
        // No se usa actualizarCamposEnFirestore porque esa función swallow errores.
        try {
          const docRef = doc(db, "pacientes", String(idPaciente));
          await updateDoc(docRef, { archivos: arrayUnion(archivo) });
        } catch (e) {
          // Rollback local: sacar el archivo del estado para que no aparezca como guardado
          paciente.archivos = paciente.archivos.filter(
            (a) => a.storageRef !== archivo.storageRef
          );
          this.guardarEnLocalStorage();
          console.warn("No se pudo guardar la referencia del archivo en Firestore:", e);
          throw e; // Propagar para que el caller limpie Storage
        }
      }
    },

    async eliminarArchivo(idPaciente, index) {
      if (!this._verificarConexion()) return;
      const paciente = this.pacientes.find((p) => p.id === idPaciente);
      if (paciente && paciente.archivos) {
        const archivo = paciente.archivos[index];
        // Eliminar de Firebase Storage si tiene referencia (archivos nuevos)
        if (archivo && archivo.storageRef) {
          try {
            await deleteObject(storageRef(firebaseStorage, archivo.storageRef));
          } catch (e) {
            // Si ya no existe en Storage, igual eliminamos el registro
            console.warn("No se pudo eliminar de Storage:", e);
          }
        }
        paciente.archivos.splice(index, 1);
        this.guardarEnLocalStorage();
        this.actualizarCamposEnFirestore(paciente.id, { archivos: arrayRemove(archivo) });
      }
    },

    // --- Acciones de Tratamientos Globales ---
    agregarTratamientoGlobal(tratamiento) {
      if (!this._verificarConexion()) return;
      // El precio no puede ser negativo ni inválido (dejaría el total del presupuesto bajo cero).
      const precio = Number(tratamiento.precio);
      if (!Number.isFinite(precio) || precio < 0) {
        const { error: toastError } = useToast();
        toastError("El precio del tratamiento no puede ser negativo.");
        return;
      }
      const usuariosStore = useUsuariosStore();
      const usuarioId = usuariosStore.usuarioActual?.id || usuariosStore.usuarioActual?.uid;
      const nuevoTratamiento = {
        id: crypto.randomUUID(),
        nombre: tratamiento.nombre,
        precio,
        usuarioId: usuarioId || null,
      };
      this.tratamientos.push(nuevoTratamiento);
      this.guardarEnLocalStorage();
      this.guardarTratamientoEnFirestore(nuevoTratamiento);
      return true;
    },

    editarTratamientoGlobal(id, datos) {
      if (!this._verificarConexion()) return;
      // Mismo resguardo que al crear: no aceptar un precio negativo o inválido.
      if (datos.precio !== undefined) {
        const precio = Number(datos.precio);
        if (!Number.isFinite(precio) || precio < 0) {
          const { error: toastError } = useToast();
          toastError("El precio del tratamiento no puede ser negativo.");
          return;
        }
        datos = { ...datos, precio };
      }
      const usuariosStore = useUsuariosStore();
      const usuarioId = usuariosStore.usuarioActual?.id || usuariosStore.usuarioActual?.uid;
      const index = this.tratamientos.findIndex(
        (t) => t.id === id && (!t.usuarioId || t.usuarioId === usuarioId)
      );
      if (index !== -1) {
        Object.assign(this.tratamientos[index], datos);
        this.guardarEnLocalStorage();
        this.guardarTratamientoEnFirestore(this.tratamientos[index]);
        return true;
      }
    },

    eliminarTratamientoGlobal(id) {
      if (!this._verificarConexion()) return;
      const usuariosStore = useUsuariosStore();
      const usuarioId = usuariosStore.usuarioActual?.id || usuariosStore.usuarioActual?.uid;
      this.tratamientos = this.tratamientos.filter(
        (t) => !(t.id === id && (!t.usuarioId || t.usuarioId === usuarioId))
      );
      this.guardarEnLocalStorage();
      this.eliminarTratamientoDeFirestore(id);
    },

    // ===== Planes de tratamiento (Opción C) =====

    // Aplica una mutación a los planes del paciente: optimista en local + transacción
    // atómica en Firestore (que además migra el formato viejo la primera vez que se toca).
    // `aplicar` recibe un objeto { presupuestos, proximoPlanNum } y lo muta POR ID, para
    // que sirva igual sobre el estado local y sobre lo que haya en el servidor. Si algo
    // falla, revierte el cambio local (rollback) y avisa.
    async _persistirPlanes(idPaciente, aplicar, rollbackMsg) {
      if (!this._verificarConexion()) return false;
      const paciente = this.pacientes.find((p) => p.id === idPaciente);
      if (!paciente) return false;
      if (!Array.isArray(paciente.presupuestos)) paciente.presupuestos = [];
      const backup = JSON.parse(JSON.stringify(paciente.presupuestos));
      const backupNum = paciente.proximoPlanNum;

      if (aplicar(paciente) === false) return false; // optimista local
      this.guardarEnLocalStorage();

      try {
        const docRef = doc(db, "pacientes", String(idPaciente));
        await runTransaction(db, async (tx) => {
          const snap = await tx.get(docRef);
          if (!snap.exists()) return;
          const { presupuestos, proximoPlanNum } = normalizarPlanes(snap.data());
          const wrapper = { presupuestos, proximoPlanNum };
          if (aplicar(wrapper) === false) return;
          tx.update(docRef, {
            presupuestos: wrapper.presupuestos,
            proximoPlanNum: wrapper.proximoPlanNum,
            presupuesto: deleteField(), // migra el formato viejo al tocar por primera vez
          });
        });
        return true;
      } catch (e) {
        paciente.presupuestos = backup;
        paciente.proximoPlanNum = backupNum;
        this.guardarEnLocalStorage();
        console.warn("No se pudo guardar el plan en la nube:", e);
        const { error: toastError } = useToast();
        toastError(rollbackMsg || "No se pudo guardar el cambio en la nube. Intentá de nuevo.");
        return false;
      }
    },

    // Crea un plan nuevo (borrador). Devuelve el id del plan creado (para seleccionarlo).
    crearPlan(idPaciente) {
      let creado = null;
      this._persistirPlanes(idPaciente, (t) => {
        if (!creado) creado = crearPlanVacio(Number(t.proximoPlanNum) || 1);
        opAddPlan(t.presupuestos, JSON.parse(JSON.stringify(creado)));
        t.proximoPlanNum = Math.max(Number(t.proximoPlanNum) || 1, parseInt(creado.num, 10) + 1);
        return true;
      }, "No se pudo crear el plan.");
      return creado ? creado.id : null;
    },

    deshacerPlan(idPaciente, planId) {
      return this._persistirPlanes(idPaciente,
        (t) => opRemovePlan(t.presupuestos, planId),
        "No se pudo deshacer el plan.");
    },

    cambiarEstadoPlan(idPaciente, planId, estado) {
      return this._persistirPlanes(idPaciente,
        (t) => opSetEstado(t.presupuestos, planId, estado),
        "No se pudo cambiar el estado del plan.");
    },

    // Agrega un tratamiento de la biblioteca al plan (copia congelada de nombre+precio).
    agregarTratamientoAlPlan(idPaciente, planId, tratamiento) {
      let item = null;
      this._persistirPlanes(idPaciente, (t) => {
        if (!item) item = crearItemDeTratamiento(tratamiento);
        return opAddItem(t.presupuestos, planId, JSON.parse(JSON.stringify(item)));
      }, "No se pudo agregar el tratamiento.");
      return item ? item.id : null;
    },

    quitarTratamientoDelPlan(idPaciente, planId, itemId) {
      return this._persistirPlanes(idPaciente,
        (t) => opRemoveItem(t.presupuestos, planId, itemId),
        "No se pudo quitar el tratamiento.");
    },

    actualizarDescuentoEnPlan(idPaciente, planId, itemId, pct) {
      return this._persistirPlanes(idPaciente,
        (t) => opSetDescuento(t.presupuestos, planId, itemId, pct),
        "No se pudo actualizar el descuento.");
    },

    // Pago individual a un tratamiento del plan.
    registrarPagoEnPlan(idPaciente, planId, itemId, monto, metodo) {
      if (!(Number(monto) > 0)) return false;
      const pago = nuevoPago(monto, metodo);
      return this._persistirPlanes(idPaciente,
        (t) => opAddPagos(t.presupuestos, planId, [{ itemId, pago: JSON.parse(JSON.stringify(pago)) }]),
        "No se pudo registrar el pago.");
    },

    // Cobro en bloque: reparte `monto` en cascada entre los itemIds (en orden).
    // La distribución se calcula desde el estado local y se aplica por id.
    cobrarPlanEnBloque(idPaciente, planId, itemIds, monto, metodo) {
      const paciente = this.pacientes.find((p) => p.id === idPaciente);
      const plan = paciente && (paciente.presupuestos || []).find((pl) => pl.id === planId);
      if (!plan) return false;
      const { pagos } = distribuirCascada(plan, itemIds, monto, metodo);
      if (!pagos.length) return false;
      const payload = pagos.map((x) => ({ itemId: x.itemId, pago: JSON.parse(JSON.stringify(x.pago)) }));
      return this._persistirPlanes(idPaciente,
        (t) => opAddPagos(t.presupuestos, planId, payload),
        "No se pudo registrar el cobro.");
    },

    eliminarPagoDelPlan(idPaciente, planId, itemId, pagoId) {
      return this._persistirPlanes(idPaciente,
        (t) => opRemovePago(t.presupuestos, planId, itemId, pagoId),
        "No se pudo eliminar el pago.");
    },

    moverPagoEnPlan(idPaciente, planId, fromItemId, toItemId, pagoId) {
      return this._persistirPlanes(idPaciente,
        (t) => opMovePago(t.presupuestos, planId, fromItemId, toItemId, pagoId),
        "No se pudo mover el pago.");
    },

    eliminarPaciente(id) {
      if (!this._verificarConexion()) return;
      const usuariosStore = useUsuariosStore();
      const usuarioId = usuariosStore.usuarioActual?.id || usuariosStore.usuarioActual?.uid;
      // Verificar que el paciente pertenece al usuario actual antes de eliminar
      const paciente = this.pacientes.find((p) => p.id === id && p.usuarioId === usuarioId);
      if (!paciente) return;
      this.pacientes = this.pacientes.filter((p) => p.id !== id);
      this.guardarEnLocalStorage();
      this.eliminarPacienteDeFirestore(id);
    },

    toggleEstadoPaciente(id) {
      if (!this._verificarConexion()) return;
      const paciente = this.pacientes.find((p) => p.id === id);
      if (paciente) {
        paciente.activo = paciente.activo === false ? true : false;
        this.guardarEnLocalStorage();
        this.actualizarCamposEnFirestore(id, { activo: paciente.activo });
      }
    },
  },
});
